import {
  assert,
  isLessThanOrEqualWithinFloatingPointTolerance,
} from "@lib/utils/utils";
import Matrix from "./matrix";
import { unilateralAllocInto } from "./unilateralAlloc";
import type Vector from "./vector";

type AccountsComputationScratch = {
  columnValues: Float64Array;
  columnRows: Int32Array;
  sortedValues: Float64Array;
  uniqueAmounts: Float64Array;
  contributors: Int32Array;
};

function createAccountsComputationScratch(
  numEvaluators: number,
): AccountsComputationScratch {
  return {
    columnValues: new Float64Array(numEvaluators),
    columnRows: new Int32Array(numEvaluators),
    sortedValues: new Float64Array(numEvaluators),
    uniqueAmounts: new Float64Array(numEvaluators),
    contributors: new Int32Array(numEvaluators),
  };
}

function computeAccountsOfGuarantees(
  guarantees: Matrix<"evaluator", "evaluable+hold">,
  responsibilityProportions: Float64Array,
  accounts: Matrix<"evaluator", "evaluable+hold">,
  rowSums: Float64Array,
  scratch: AccountsComputationScratch,
): void {
  const numEvaluators = guarantees.n_rows;
  const numEvaluables = guarantees.n_cols;
  const guaranteesData = guarantees.data;
  const accountsData = accounts.data;
  accountsData.fill(0);
  rowSums.fill(0);
  const {
    columnValues,
    columnRows,
    sortedValues,
    uniqueAmounts,
    contributors,
  } = scratch;

  for (let colNo = 0; colNo < numEvaluables; colNo++) {
    // Gather the column's nonzero entries (in row order). Zero guarantees can
    // never contribute to a positive shared amount, so the sort/unique/sharing
    // passes below only need the nonzero subset — guarantee columns are
    // typically sparse (few evaluators fund a given evaluable), and this loop
    // runs once per evaluable per epoch. Negative guarantees (impossible for
    // cumulative tranche inputs, but cheap to guard) would break that
    // assumption, so fall back to considering every entry.
    let activeCount = 0;
    let hasNegative = false;
    for (let rowNo = 0; rowNo < numEvaluators; rowNo++) {
      const value = guaranteesData[rowNo * numEvaluables + colNo]!;
      if (value !== 0) {
        columnValues[activeCount] = value;
        columnRows[activeCount] = rowNo;
        activeCount++;
        if (value < 0) {
          hasNegative = true;
        }
      }
    }

    if (activeCount === 0) {
      continue;
    }

    if (hasNegative) {
      activeCount = numEvaluators;
      for (let rowNo = 0; rowNo < numEvaluators; rowNo++) {
        columnValues[rowNo] = guaranteesData[rowNo * numEvaluables + colNo]!;
        columnRows[rowNo] = rowNo;
      }
    }

    // Insertion sort: active counts are small (tens at most), where this beats
    // the native typed-array sort's per-call overhead.
    for (let i = 0; i < activeCount; i++) {
      const value = columnValues[i]!;
      let j = i - 1;
      while (j >= 0 && sortedValues[j]! > value) {
        sortedValues[j + 1] = sortedValues[j]!;
        j--;
      }
      sortedValues[j + 1] = value;
    }

    let uniqueCount = 0;
    let hasPrevious = false;
    let previousSortedValue = 0;
    for (let i = 0; i < activeCount; i++) {
      const value = sortedValues[i]!;
      if (!hasPrevious || value !== previousSortedValue) {
        uniqueAmounts[uniqueCount] = value;
        uniqueCount++;
        previousSortedValue = value;
        hasPrevious = true;
      }
    }

    let previousAmount = 0;
    for (let uniqueNo = 0; uniqueNo < uniqueCount; uniqueNo++) {
      const amount = uniqueAmounts[uniqueNo]!;
      const sharedAmount = amount - previousAmount;
      if (sharedAmount === 0) {
        previousAmount = amount;
        continue;
      }

      let contributorCount = 0;
      let totalResponsibility = 0;
      for (let activeNo = 0; activeNo < activeCount; activeNo++) {
        if (columnValues[activeNo]! >= amount) {
          const rowNo = columnRows[activeNo]!;
          contributors[contributorCount] = rowNo;
          contributorCount++;
          totalResponsibility += responsibilityProportions[rowNo]!;
        }
      }

      if (contributorCount === 0 || totalResponsibility === 0) {
        previousAmount = amount;
        continue;
      }

      const scale = sharedAmount / totalResponsibility;
      for (
        let contributorNo = 0;
        contributorNo < contributorCount;
        contributorNo++
      ) {
        const contributor = contributors[contributorNo]!;
        const contribution = responsibilityProportions[contributor]! * scale;
        const index = contributor * numEvaluables + colNo;
        accountsData[index] = accountsData[index]! + contribution;
        rowSums[contributor] = rowSums[contributor]! + contribution;
      }

      previousAmount = amount;
    }
  }
}

export function accountsOfGuarantees(
  guarantees: Matrix<"evaluator", "evaluable+hold">, // (evaluators may include hold)
  responsibilityProportions: Vector<"evaluator">,
): Matrix<"evaluator", "evaluable+hold"> {
  assert(
    guarantees.n_rows === responsibilityProportions.length,
    "responsibilityProportions length must equal guarantees.n_rows",
  );
  const accounts = Matrix.zeros<"evaluator", "evaluable+hold">(
    guarantees.n_rows,
    guarantees.n_cols,
  );
  const accountRowSums = new Float64Array(guarantees.n_rows);
  computeAccountsOfGuarantees(
    guarantees,
    responsibilityProportions.data,
    accounts,
    accountRowSums,
    createAccountsComputationScratch(guarantees.n_rows),
  );
  return accounts;
}

function isCloseEnough(
  maxRemainingBudgetValue: number,
  maxRemainingBudget: number,
) {
  return maxRemainingBudgetValue <= maxRemainingBudget;
}

function maxOf(values: Float64Array): number {
  if (values.length === 0) {
    return 0;
  }

  let max = values[0]!;
  for (let i = 1; i < values.length; i++) {
    const value = values[i]!;
    if (value > max) {
      max = value;
    }
  }
  return max;
}

function sumOf(values: Float64Array): number {
  let sum = 0;
  for (let i = 0; i < values.length; i++) {
    sum += values[i]!;
  }
  return sum;
}

function rowSums(matrix: Matrix<"evaluator", "evaluable+hold">): Float64Array {
  const sums = new Float64Array(matrix.n_rows);
  const { data, n_cols: numEvaluables } = matrix;
  for (let rowNo = 0; rowNo < matrix.n_rows; rowNo++) {
    let sum = 0;
    const offset = rowNo * numEvaluables;
    for (let colNo = 0; colNo < numEvaluables; colNo++) {
      sum += data[offset + colNo]!;
    }
    sums[rowNo] = sum;
  }
  return sums;
}

function numericStats(values: ArrayLike<number>): Record<string, unknown> {
  let finiteCount = 0;
  let negativeCount = 0;
  let nonFiniteCount = 0;
  let min = Infinity;
  let max = -Infinity;
  let sum = 0;

  for (let i = 0; i < values.length; i++) {
    const value = values[i]!;
    if (!Number.isFinite(value)) {
      nonFiniteCount++;
      continue;
    }

    finiteCount++;
    if (value < 0) {
      negativeCount++;
    }
    if (value < min) {
      min = value;
    }
    if (value > max) {
      max = value;
    }
    sum += value;
  }

  return {
    length: values.length,
    finiteCount,
    nonFiniteCount,
    negativeCount,
    min: finiteCount > 0 ? min : null,
    max: finiteCount > 0 ? max : null,
    sum,
  };
}

function matrixStats(matrix: Matrix<string, string>): Record<string, unknown> {
  return {
    rows: matrix.n_rows,
    cols: matrix.n_cols,
    values: numericStats(matrix.data),
  };
}

function matrixRowStats(
  matrix: Matrix<string, string>,
  rowNo: number,
): Record<string, unknown> {
  const values = new Float64Array(matrix.n_cols);
  const offset = rowNo * matrix.n_cols;
  for (let colNo = 0; colNo < matrix.n_cols; colNo++) {
    values[colNo] = matrix.data[offset + colNo]!;
  }
  return numericStats(values);
}

function trancheTableDiagnostics(
  table: Matrix<"tranche", "evaluable+hold"> | undefined,
): Record<string, unknown> | null {
  if (table == null) {
    return null;
  }

  const rowSumValues = new Float64Array(table.n_rows);
  let nonMonotoneRowSumCount = 0;
  for (let rowNo = 0; rowNo < table.n_rows; rowNo++) {
    let rowSum = 0;
    const offset = rowNo * table.n_cols;
    for (let colNo = 0; colNo < table.n_cols; colNo++) {
      rowSum += table.data[offset + colNo]!;
    }
    rowSumValues[rowNo] = rowSum;
    if (rowNo > 0 && rowSum < rowSumValues[rowNo - 1]!) {
      nonMonotoneRowSumCount++;
    }
  }

  return {
    ...matrixStats(table),
    rowSums: numericStats(rowSumValues),
    nonMonotoneRowSumCount,
  };
}

function getResponsibilityProportions(
  evaluatorBudgets: Vector<"evaluator">,
  numEvaluators: number,
): Float64Array {
  const totalBudget = evaluatorBudgets.sum();
  const proportions = new Float64Array(numEvaluators);

  if (totalBudget === 0) {
    const proportion = numEvaluators === 0 ? 0 : 1 / numEvaluators;
    proportions.fill(proportion);
    return proportions;
  }

  const budgetsData = evaluatorBudgets.data;
  for (let evaluatorNo = 0; evaluatorNo < numEvaluators; evaluatorNo++) {
    proportions[evaluatorNo] = budgetsData[evaluatorNo]! / totalBudget;
  }
  return proportions;
}

// The default convergence tolerance: iteration stops once every evaluator's
// unallocated budget is at or below this many dollars. Exported so callers
// that seed the solver with initialGuarantees can tell when a budget delta is
// within the tolerance — such a solve can look converged before running an
// epoch, silently freezing the answer at the seed.
export const DEFAULT_MAX_REMAINING_BUDGET = 1000;

/** Implement the Algorithm described in [On the S-Process](https://www.overleaf.com/project/65fa09a01060a6a884ab48e2).
 *
 * @param evaluatorBudgets - The budgets of each evaluator.
 * @param trancheTables - Tranche tables for each evaluator. This is a list of
 * tranche tables (one per evaluator). Each of those an n_tranch x n_evaluables
 * matrix. These should be *cumulative* tranche tables (not marginal).
 * @returns The final accounts and guarantees. These are both n_evaluators x
 * n_evaluables matrices.
 */
export function singleLayerSProcess(
  evaluatorBudgets: Vector<"evaluator">, // (evaluators may include hold)
  trancheTables: Matrix<"tranche", "evaluable+hold">[],
  {
    debugLabel,
    maxEpochs = 75,
    maxRemainingBudget = DEFAULT_MAX_REMAINING_BUDGET,
    initialGuarantees,
  }: {
    debugLabel?: string;
    maxEpochs?: number;
    maxRemainingBudget?: number;
    // Initialize guarantees to this matrix (as an optimization if you expect
    // the solution to be close to a previous solution).
    initialGuarantees?: Matrix<"evaluator", "evaluable+hold">;
  } = {},
): {
  accounts: Matrix<"evaluator", "evaluable+hold">;
  guarantees: Matrix<"evaluator", "evaluable+hold">;
  epochs: number;
  remainingBudget: number;
} {
  const numEvaluators = trancheTables.length;
  const numEvaluables = trancheTables[0]?.shape[1] ?? 0;
  const evaluatorBudgetsData = evaluatorBudgets.data;
  const responsibilityProportions = getResponsibilityProportions(
    evaluatorBudgets,
    numEvaluators,
  );

  let guarantees = initialGuarantees;
  if (guarantees == null) {
    guarantees = Matrix.zeros<"evaluator", "evaluable+hold">(
      numEvaluators,
      numEvaluables,
    );
  } else {
    const copiedGuarantees = Matrix.zeros<"evaluator", "evaluable+hold">(
      numEvaluators,
      numEvaluables,
    );
    copiedGuarantees.data.set(guarantees.data);
    guarantees = copiedGuarantees;
  }

  const accounts = Matrix.zeros<"evaluator", "evaluable+hold">(
    numEvaluators,
    numEvaluables,
  );
  const accountRowSums = new Float64Array(numEvaluators);
  const accountComputationScratch =
    createAccountsComputationScratch(numEvaluators);
  computeAccountsOfGuarantees(
    guarantees,
    responsibilityProportions,
    accounts,
    accountRowSums,
    accountComputationScratch,
  );
  const guaranteeRowSums = rowSums(guarantees);
  const budgetAdjustment = new Float64Array(numEvaluators);

  const remainingBudget = new Float64Array(numEvaluators);
  for (let evaluatorNo = 0; evaluatorNo < numEvaluators; evaluatorNo++) {
    remainingBudget[evaluatorNo] =
      evaluatorBudgetsData[evaluatorNo]! - accountRowSums[evaluatorNo]!;
  }
  let maxRemainingBudgetValue = maxOf(remainingBudget);

  let epochs = 0;
  // TODO(SP-487): make convergence threshold 1 again.
  while (
    !isCloseEnough(maxRemainingBudgetValue, maxRemainingBudget) &&
    epochs < maxEpochs
  ) {
    epochs++;
    const guaranteesData = guarantees.data;

    for (let evaluatorNo = 0; evaluatorNo < numEvaluators; evaluatorNo++) {
      const newBudget =
        evaluatorBudgetsData[evaluatorNo]! +
        (guaranteeRowSums[evaluatorNo]! - accountRowSums[evaluatorNo]!) +
        budgetAdjustment[evaluatorNo]!;

      const rowOffset = evaluatorNo * numEvaluables;
      guaranteeRowSums[evaluatorNo] = unilateralAllocInto(
        trancheTables[evaluatorNo]!,
        newBudget,
        guaranteesData,
        rowOffset,
      );
    }
    computeAccountsOfGuarantees(
      guarantees,
      responsibilityProportions,
      accounts,
      accountRowSums,
      accountComputationScratch,
    );

    for (let evaluatorNo = 0; evaluatorNo < numEvaluators; evaluatorNo++) {
      remainingBudget[evaluatorNo] =
        evaluatorBudgetsData[evaluatorNo]! - accountRowSums[evaluatorNo]!;
    }
    maxRemainingBudgetValue = maxOf(remainingBudget);
  }

  assert(
    accounts.n_rows === numEvaluators,
    "accounts.n_rows should be equal to numEvaluators",
  );
  assert(
    guarantees.n_rows === numEvaluators,
    "guarantees.n_rows should be equal to numEvaluators",
  );
  assert(
    accounts.n_cols === numEvaluables,
    "accounts.n_cols should be equal to numEvaluables",
  );
  assert(
    guarantees.n_cols === numEvaluables,
    "guarantees.n_cols should be equal to numEvaluables",
  );

  // If using an initialGuarantees matrix, we can't guarantee that no evaluator
  // will overspend.
  if (!initialGuarantees) {
    for (let i = 0; i < accounts.n_rows; i++) {
      const getPostconditionDiagnostics = () => ({
        debugLabel,
        evaluatorNo: i,
        numEvaluators,
        numEvaluables,
        epochs,
        maxEpochs,
        maxRemainingBudget,
        maxRemainingBudgetValue,
        evaluatorBudget: evaluatorBudgetsData[i],
        responsibilityProportion: responsibilityProportions[i],
        accountsSum: accountRowSums[i],
        guaranteesSum: guaranteeRowSums[i],
        guaranteeMinusAccounts: guaranteeRowSums[i]! - accountRowSums[i]!,
        remainingBudget: remainingBudget[i],
        allEvaluatorBudgets: numericStats(evaluatorBudgetsData),
        allRemainingBudgets: numericStats(remainingBudget),
        evaluatorAccountsRow: matrixRowStats(accounts, i),
        evaluatorGuaranteesRow: matrixRowStats(guarantees, i),
        evaluatorTrancheTable: trancheTableDiagnostics(trancheTables[i]),
      });

      assert(
        remainingBudget[i]! >= -10,
        "remainingBudget should be non-negative",
        getPostconditionDiagnostics,
      );
      const accountsSum = accountRowSums[i]!;
      const guaranteesSum = guaranteeRowSums[i]!;
      assert(
        isLessThanOrEqualWithinFloatingPointTolerance(
          accountsSum,
          guaranteesSum,
        ),
        "accountsSum should be less than or equal to guaranteesSum",
        getPostconditionDiagnostics,
      );
      assert(
        accountsSum <= evaluatorBudgetsData[i]! + 10,
        "accountsSum should be less than or equal to evaluatorBudgets",
        getPostconditionDiagnostics,
      );
    }
  }

  return {
    accounts,
    guarantees,
    epochs,
    remainingBudget: isCloseEnough(maxRemainingBudgetValue, maxRemainingBudget)
      ? 0
      : sumOf(remainingBudget),
  };
}
